// API Service Layer - Connects React frontend to PHP backend
const API_BASE = import.meta.env.VITE_API_URL || '/api';

class ApiClient {
    constructor() {
        this.baseUrl = API_BASE;
    }

    getToken() {
        return localStorage.getItem('provatio_token');
    }

    setToken(token) {
        localStorage.setItem('provatio_token', token);
    }

    clearToken() {
        localStorage.removeItem('provatio_token');
        localStorage.removeItem('provatio_user');
    }

    async request(endpoint, options = {}) {
        const token = this.getToken();
        const headers = {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            ...options.headers,
        };

        const url = `${this.baseUrl}/${endpoint}`;

        try {
            const response = await fetch(url, { ...options, headers });
            const data = await response.json();

            if (!response.ok) {
                if (response.status === 401) {
                    this.clearToken();
                    window.dispatchEvent(new CustomEvent('auth:expired'));
                }
                throw { status: response.status, ...data };
            }
            return data;
        } catch (error) {
            if (error.status) throw error;
            console.error('API request failed:', error);
            throw { status: 0, error: 'Falha na conexão com o servidor' };
        }
    }

    // Auth
    auth = {
        login: async (email, password) => {
            const data = await this.request('auth.php?action=login', {
                method: 'POST',
                body: JSON.stringify({ email, password }),
            });
            if (data.token) {
                this.setToken(data.token);
                localStorage.setItem('provatio_user', JSON.stringify(data.user));
            }
            return data;
        },
        me: async () => this.request('auth.php?action=me'),
        logout: async () => this.clearToken()
    };

    // Dashboard
    dashboard = {
        get: async () => this.request('pericias.php?action=dashboard'),
    };

    // Processos
    processos = {
        list: async (params = {}) => {
            const q = new URLSearchParams(params).toString();
            return this.request(`pericias.php?action=processos&${q}`);
        },
        get: async (id) => this.request(`pericias.php?action=processo&id=${id}`),
        create: async (data) => this.request('pericias.php?action=processos', { method: 'POST', body: JSON.stringify(data) }),
        update: async (id, data) => this.request(`pericias.php?action=processo&id=${id}`, { method: 'PUT', body: JSON.stringify(data) }),
        delete: async (id) => this.request(`pericias.php?action=processo&id=${id}`, { method: 'DELETE' })
    };

    // Laudos
    laudos = {
        list: async (params = {}) => {
            const q = new URLSearchParams(params).toString();
            return this.request(`pericias.php?action=laudos&${q}`);
        },
        get: async (id) => this.request(`pericias.php?action=laudo&id=${id}`),
        create: async (data) => this.request('pericias.php?action=laudos', { method: 'POST', body: JSON.stringify(data) }),
        update: async (id, data) => this.request(`pericias.php?action=laudo&id=${id}`, { method: 'PUT', body: JSON.stringify(data) }),
        delete: async (id) => this.request(`pericias.php?action=laudo&id=${id}`, { method: 'DELETE' })
    };

    // Honorários
    honorarios = {
        list: async (params = {}) => {
            const q = new URLSearchParams(params).toString();
            return this.request(`pericias.php?action=honorarios&${q}`);
        },
        update: async (id, data) => this.request(`pericias.php?action=honorario&id=${id}`, { method: 'PUT', body: JSON.stringify(data) }),
        create: async (data) => this.request('pericias.php?action=honorarios', { method: 'POST', body: JSON.stringify(data) }),
        delete: async (id) => this.request(`pericias.php?action=honorario&id=${id}`, { method: 'DELETE' })
    };

    // Despesas
    despesas = {
        list: async (params = {}) => {
            const q = new URLSearchParams(params).toString();
            return this.request(`pericias.php?action=despesas&${q}`);
        },
        create: async (data) => this.request('pericias.php?action=despesas', { method: 'POST', body: JSON.stringify(data) }),
        update: async (id, data) => this.request(`pericias.php?action=despesa&id=${id}`, { method: 'PUT', body: JSON.stringify(data) }),
        delete: async (id) => this.request(`pericias.php?action=despesa&id=${id}`, { method: 'DELETE' })
    };

    // Auditoria
    auditoria = {
        list: async (params = {}) => {
            const q = new URLSearchParams(params).toString();
            return this.request(`pericias.php?action=auditoria&${q}`);
        }
    };

    // System Utils
    utils = {
        runMigration: async () => this.request('migrate.php'),
        runSeed: async (key = 'provatio_seed_2026') => this.request(`seed.php?key=${key}`)
    };
}

export const api = new ApiClient();
export default api;
